import React, { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { reportScorerConcentration, reportTrend } from '../../utils/rivalReport';

export default function ReportInsights({ statistics, standing, recent, players }) {
  const { t, i18n } = useTranslation('reports');
  const id = useId();
  const trend = reportTrend(recent, standing);
  const scorers = reportScorerConcentration(players, statistics, standing);
  const format = value => value === null ? t('modern.insights.unknown')
    : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const delta = trend.recent.pointsRate === null || trend.cumulative.pointsRate === null
    ? null : trend.recent.pointsRate - trend.cumulative.pointsRate;
  const columns = [trend.recent, trend.cumulative];
  const rows = [
    ['stats.played', 'played'], ['stats.points', 'points'],
    ['stats.goalsFor', 'goalsFor'], ['stats.goalsAgainst', 'goalsAgainst'],
    ['modern.pointsRate', 'pointsRate'], ['modern.goalsForRate', 'goalsForRate'], ['modern.goalsAgainstRate', 'goalsAgainstRate'],
  ];
  return <div className="rr-insights-grid">
    <section className="rr-panel rr-insight" aria-labelledby={`${id}-trend`}>
      <header><span className="rr-eyebrow">{t('modern.insights.eyebrow')}</span>
        <h2 id={`${id}-trend`}>{t('modern.insights.trend')}</h2>
        <p>{t('modern.insights.trendHelp', { count: trend.recent.played })}</p></header>
      <div className="rr-data-table"><table aria-label={t('modern.insights.trend')}>
        <thead><tr><th>{t('modern.insights.metric')}</th>
          <th>{t('modern.insights.recent', { count: trend.recent.played })}</th><th>{t('modern.insights.cumulative')}</th></tr></thead>
        <tbody>{rows.map(([key, field]) => {
          const maximum = Math.max(1, ...columns.map(column => column[field] ?? 0));
          return <tr key={field}><th scope="row">{t(key)}</th>
            {columns.map((column, index) => <td key={index} className={field.endsWith('Rate') ? 'rr-insight-rate' : undefined}>
              {format(column[field])}
              {field.endsWith('Rate') && column[field] !== null && <i aria-hidden="true"
                className={index === 0 ? 'rr-insight-recent' : 'rr-insight-cumulative'}
                style={{ width: `${column[field] / maximum * 88}%` }} />}
            </td>)}</tr>;
        })}</tbody>
      </table></div>
      <div className="rr-insight-delta"><span>{t('modern.insights.delta')}</span><strong>{delta === null
        ? format(null) : delta.toLocaleString(i18n.language, { maximumFractionDigits: 2, signDisplay: 'exceptZero' })}</strong></div>
      <p className="rr-insight-method">{t('modern.insights.trendMethod')}</p>
    </section>
    <section className="rr-panel rr-insight" aria-labelledby={`${id}-scorers`}>
      <header><span className="rr-eyebrow">{t('modern.insights.eyebrow')}</span>
        <h2 id={`${id}-scorers`}>{t('modern.insights.scorers')}</h2>
        <p>{t('modern.insights.scorersHelp')}</p></header>
      <div className="rr-insight-share"><div><span>{t('modern.insights.topThree')}</span>
        <strong className={scorers.share === null ? 'rr-insight-unavailable' : undefined}>
          {scorers.share === null ? t('modern.insights.unverified') : `${format(scorers.share)} %`}</strong></div>
        <span>{t('modern.insights.registered', { goals: format(scorers.topGoals) })}</span>
        {scorers.share !== null && <div className="rr-insight-share-track" aria-hidden="true"><i style={{ width: `${scorers.share}%` }} /></div>}
      </div>
      <div className="rr-data-table"><table aria-label={t('modern.insights.scorers')}>
        <thead><tr><th>{t('modern.insights.scorer')}</th><th>{t('table.goals')}</th></tr></thead>
        <tbody>{scorers.leaders.map((player, index) => <tr key={index}>
          <th scope="row"><span className="rr-insight-rank" aria-hidden="true">{index + 1}</span>{player.name || t('modern.insights.unknownName')}</th>
          <td>{format(player.goals)}</td></tr>)}
          <tr><th scope="row">{t('modern.insights.remainder')}</th><td>{format(scorers.others)}</td></tr>
          <tr><th scope="row">{t('modern.insights.total')}</th><td>{format(scorers.total)}</td></tr>
        </tbody>
      </table></div>
      <p className="rr-insight-method">{t(scorers.covered ? 'modern.insights.reconciled' : 'modern.insights.notReconciled')}</p>
    </section>
  </div>;
}
